import type { BootstrapConfig } from '../api/types';
import type { BuilderStep } from '../types/models';
import type { BuilderStore } from '../state/BuilderStore';
import { fetchModelDetail, resolveImageUrl } from '../api/client';
import { formatCurrency } from '../pricing/PriceFormatter';
import { parseSettings } from '../config/BuilderConfig';

/**
 * Engine data shape from the model detail API.
 */
interface EngineData {
    id: number;
    engine_name: string;
    horsepower: number;
    description: string;
    msrp_price: number;
    sale_price: number;
    dealer_net: number;
    thumbnail: string;
    features: { id: number; feature: string }[];
}

/**
 * Step 5 – Engine: shows available engines for the selected model.
 * Exactly one engine must be chosen.
 */
export class StepEngine implements BuilderStep {
    readonly id = 'engine';
    readonly label = 'Engine';

    private container: HTMLElement | null = null;
    private engines: EngineData[] = [];

    constructor(
        private store: BuilderStore,
        private config: BootstrapConfig
    ) { }

    isEnabled(_config: BootstrapConfig): boolean {
        return true;
    }

    async render(container: HTMLElement): Promise<void> {
        this.container = container;
        const modelId = this.store.getState().selectedModelId;

        if (!modelId) {
            container.innerHTML = `<div class="vc-step"><p class="vc-warning">No model selected.</p></div>`;
            return;
        }

        container.innerHTML = `
      <div class="vc-step vc-step-engine">
        <h2>Choose Your Engine</h2>
        <div id="vc-engine-list" class="vc-engine-list">
          <p class="vc-loading">Loading engines...</p>
        </div>
      </div>
    `;

        try {
            const detail = (await fetchModelDetail(modelId)) as { engines: EngineData[] };
            this.engines = detail.engines ?? [];
            this.renderEngines();
            this.restoreSelection();
        } catch {
            const el = container.querySelector('#vc-engine-list');
            if (el) el.innerHTML = `<p class="vc-error">Failed to load engines.</p>`;
        }
    }

    validate(): boolean {
        return this.store.getState().selectedEngineId !== null;
    }

    onEnter(): void { }

    onLeave(): void { }

    destroy(): void {
        this.container = null;
        this.engines = [];
    }

    // ── private ─────────────────────────────────────────────

    private renderEngines(): void {
        const el = this.container?.querySelector('#vc-engine-list');
        if (!el) return;

        if (this.engines.length === 0) {
            el.innerHTML = `<p class="vc-empty">No engines available for this model.</p>`;
            return;
        }

        const settings = parseSettings(this.config);
        const selectedId = this.store.getState().selectedEngineId;

        el.innerHTML = this.engines
            .map(
                e => `
      <div class="vc-engine-card ${e.id === selectedId ? 'selected' : ''}" data-engine-id="${e.id}">
        <div class="vc-engine-header">
          ${e.thumbnail ? `<img src="${resolveImageUrl(e.thumbnail)}" alt="${e.engine_name}" class="vc-engine-thumb" />` : ''}
          <div class="vc-engine-title">
            <h3>${e.engine_name}</h3>
            ${e.horsepower ? `<span class="vc-engine-hp">${e.horsepower} HP</span>` : ''}
          </div>
          <div class="vc-engine-price">
            <span>${formatCurrency(e.msrp_price, settings.locale, settings.currency)}</span>
          </div>
        </div>
        ${e.description ? `<p class="vc-engine-desc">${e.description}</p>` : ''}
        ${e.features && e.features.length > 0
                        ? `<ul class="vc-engine-features">${e.features.map(f => `<li>${f.feature}</li>`).join('')}</ul>`
                        : ''
                    }
        <button class="vc-btn vc-btn-select ${e.id === selectedId ? 'vc-btn-selected' : ''}">
          ${e.id === selectedId ? 'Selected' : 'Select'}
        </button>
      </div>
    `
            )
            .join('');

        el.querySelectorAll('.vc-engine-card').forEach(card => {
            card.addEventListener('click', () => {
                const engineId = parseInt((card as HTMLElement).dataset.engineId ?? '0', 10);
                this.selectEngine(engineId);
            });
        });
    }

    private selectEngine(engineId: number): void {
        this.store.setState({ selectedEngineId: engineId });

        // Update UI
        this.container?.querySelectorAll('.vc-engine-card').forEach(card => {
            const isSelected = parseInt((card as HTMLElement).dataset.engineId ?? '0', 10) === engineId;
            card.classList.toggle('selected', isSelected);
            const btn = card.querySelector('.vc-btn-select');
            if (btn) {
                btn.classList.toggle('vc-btn-selected', isSelected);
                btn.textContent = isSelected ? 'Selected' : 'Select';
            }
        });
    }

    private restoreSelection(): void {
        const selected = this.store.getState().selectedEngineId;
        if (selected) {
            this.selectEngine(selected);
        } else if (this.engines.length === 1) {
            // Auto-select if only one engine
            this.selectEngine(this.engines[0].id);
        }
    }
}
